// hooks/useSwipeGestures.ts — V28 round-2 NAVIGATION REWORK.
//
// ROOT-CAUSE FIX (iOS): the previous implementation used POINTER events
// (onPointerDown/Up/Cancel). On iOS WebKit — which is BOTH iOS Safari AND iOS
// Chrome, because Apple forces every iOS browser onto WebKit — the browser
// fires `pointercancel` mid-gesture the instant it claims the touch for its own
// scroll / system back-swipe. The old hook's `onPointerCancel` set tracking
// false, so the matching `onPointerUp` never navigated. Net effect: horizontal
// swipe was DEAD on real iOS. (The phone-audit harness drove `page.mouse`, which
// emits pointer events, so it never reproduced the touch path — see its own note
// at scripts/phone-audit.mjs.)
//
// The rework attaches NATIVE `touchstart`/`touchmove`/`touchend`/`touchcancel`
// listeners via a callback ref with `{ passive: false }`, so on a confirmed
// horizontal move the handler can call `preventDefault()` to CLAIM the gesture
// before WebKit hands it to its own scroller / edge-back. That claim is what
// makes the swipe reliable on iOS. MOUSE support (desktop drag) is kept on a
// separate mousedown/mousemove/mouseup path so the same physical gesture never
// double-fires.
//
// Thresholds (founder-locked):
//   - 40px tap floor       → below this on BOTH axes it's a tap; click runs.
//   - 50px swipe trigger   → horizontal page swipe + vertical swipe-up.
//   - 60px down-to-close   → swipe-down distance that dismisses the chat sheet.
//   - ~10px intent floor   → horizontal travel past which we preventDefault() on
//                            touchmove to claim the gesture.
// Strict axis: horizontal fires only when ax > ay AND ax >= 50.

import { useRef, useCallback, useEffect } from 'react';

const TAP_FLOOR = 40;      // px — below this on both axes = tap, not swipe
const SWIPE_TRIGGER = 50;  // px — horizontal page swipe + vertical swipe-up
const CLOSE_TRIGGER = 60;  // px — swipe-down distance that closes the chat sheet
const INTENT_FLOOR = 10;   // px — horizontal travel past which we claim the gesture

export interface SwipeGestureOptions {
  /** Swipe left (or right-edge tap) → "next" in the flow. */
  onSwipeLeft?: () => void;
  /** Swipe right (or left-edge tap) → "back" in the flow. */
  onSwipeRight?: () => void;
  /** Swipe up → open the chat sheet (only meaningful on flow.chat screens). */
  onSwipeUp?: () => void;
  /** Swipe down ≥60px → close the chat sheet. */
  onSwipeDown?: () => void;
  /** When true, no gesture fires (e.g. a modal owns the surface). */
  disabled?: boolean;
}

export interface SwipeGestureBinding {
  /**
   * Callback ref — spread/assign onto the single wrapping element (the Screen
   * inner column). It attaches native touch + mouse listeners on mount and
   * cleans them up on unmount / element swap, so the gesture runs on the REAL
   * touch-event code path (the one that survives iOS WebKit's pointercancel).
   */
  ref: (el: HTMLElement | null) => void;
}

/**
 * After a real swipe, suppress the synthetic `click` the browser fires on the
 * element the gesture STARTED over — so a swipe that begins on a card/button
 * doesn't ALSO activate it. One-shot, short timeout so a deliberate tap right
 * after a swipe is unaffected.
 */
function suppressNextClick(): void {
  if (typeof window === 'undefined') return;
  const handler = (ev: Event) => {
    ev.preventDefault();
    ev.stopPropagation();
    window.removeEventListener('click', handler, true);
  };
  window.addEventListener('click', handler, { capture: true });
  window.setTimeout(() => window.removeEventListener('click', handler, true), 400);
}

function hasActiveTextSelection(): boolean {
  if (typeof window === 'undefined' || typeof window.getSelection !== 'function') return false;
  return (window.getSelection()?.toString().trim().length ?? 0) > 0;
}

/**
 * Returns a callback `ref` to attach to a single wrapping element. Tracks one
 * touch (or one mouse drag) at a time; a release that didn't clear the tap floor
 * is treated as a tap and ignored so underlying click handlers still run.
 *
 * The native-touch path is the iOS-reliable primary; the mouse path is the
 * desktop equivalent. They never both fire for one physical gesture because a
 * mouse drag emits no touch events and vice-versa.
 */
export function useSwipeGestures(opts: SwipeGestureOptions): SwipeGestureBinding {
  const { onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown, disabled } = opts;

  // Keep the latest callbacks/flags in a ref so the native listeners (attached
  // once per element) always see fresh handlers without re-binding on every
  // render. Re-binding listeners each render would be wasteful and could drop a
  // gesture mid-flight.
  const optsRef = useRef(opts);
  useEffect(() => {
    optsRef.current = { onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown, disabled };
  }, [onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown, disabled]);

  // Per-gesture mutable state. Survives the gap between start and end without
  // triggering re-renders or stale-closure bugs.
  const startX = useRef(0);
  const startY = useRef(0);
  const tracking = useRef(false);
  const claimed = useRef(false); // true once we've preventDefault()'d a horizontal move

  // The element we attached to, plus a cleanup fn for its listeners. Held in
  // refs so the callback ref can detach the previous element before binding a
  // new one (or on unmount).
  const elRef = useRef<HTMLElement | null>(null);
  const detachRef = useRef<(() => void) | null>(null);

  // Shared release logic for BOTH touchend and mouseup: decide tap vs swipe and
  // fire the right callback. `dx`/`dy` are end-minus-start deltas.
  const resolveGesture = useCallback((dx: number, dy: number) => {
    const o = optsRef.current;
    if (o.disabled) return;

    const ax = Math.abs(dx);
    const ay = Math.abs(dy);

    // A tap (both axes under the floor) → let click handlers run.
    if (ax < TAP_FLOOR && ay < TAP_FLOOR) return;

    // A real swipe: suppress the synthesized click on the start element.
    suppressNextClick();

    if (ax > ay) {
      if (ax < SWIPE_TRIGGER) return;     // not far enough horizontally → ignore
      if (dx > 0) o.onSwipeRight?.();     // → back
      else o.onSwipeLeft?.();             // → next
    } else {
      if (dy < -SWIPE_TRIGGER) o.onSwipeUp?.();        // up → open chat
      else if (dy > CLOSE_TRIGGER) o.onSwipeDown?.();  // down → close chat
    }
  }, []);

  const attach = useCallback((el: HTMLElement) => {
    // --- Native TOUCH path (iOS-reliable primary) -------------------------
    const onTouchStart = (e: TouchEvent) => {
      if (optsRef.current.disabled) { tracking.current = false; return; }
      // Track a single touch only; ignore multi-touch (pinch/zoom) gestures.
      if (e.touches.length !== 1) { tracking.current = false; return; }
      const t = e.touches[0];
      startX.current = t.clientX;
      startY.current = t.clientY;
      tracking.current = true;
      claimed.current = false;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!tracking.current || optsRef.current.disabled) return;
      const t = e.touches[0];
      if (!t) return;
      const dx = t.clientX - startX.current;
      const dy = t.clientY - startY.current;
      const ax = Math.abs(dx);
      const ay = Math.abs(dy);
      // Once horizontal intent is clear, CLAIM the gesture: preventDefault()
      // stops iOS WebKit from scrolling / triggering its own back-swipe, which
      // is exactly what kept the old pointer path from ever completing on iOS.
      if (!claimed.current && ax > ay && ax > INTENT_FLOOR) {
        claimed.current = true;
      }
      if (claimed.current && e.cancelable) {
        e.preventDefault();
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (!tracking.current) return;
      tracking.current = false;
      const t = e.changedTouches[0];
      if (!t) return;
      resolveGesture(t.clientX - startX.current, t.clientY - startY.current);
    };

    const onTouchCancel = () => {
      // Unlike the old pointer path, a touchcancel here simply drops tracking;
      // because we already preventDefault()'d a claimed horizontal move on
      // touchmove, WebKit does NOT cancel a gesture we've claimed, so this only
      // fires for un-claimed (vertical/scroll) touches — correct to abandon.
      tracking.current = false;
      claimed.current = false;
    };

    // --- MOUSE path (desktop drag equivalent) -----------------------------
    // Window-level mouseup so a drag that ends off the element still resolves.
    // No preventDefault needed for mouse — desktop has no competing OS
    // back-swipe; we just measure the start→end delta on mouseup.
    const onMouseUp = (e: MouseEvent) => {
      if (!tracking.current) return;
      tracking.current = false;
      window.removeEventListener('mouseup', onMouseUp, true);
      if (hasActiveTextSelection()) {
        claimed.current = false;
        return;
      }
      resolveGesture(e.clientX - startX.current, e.clientY - startY.current);
    };

    const onMouseDown = (e: MouseEvent) => {
      if (optsRef.current.disabled) { tracking.current = false; return; }
      if (e.button !== 0) return; // primary button only
      startX.current = e.clientX;
      startY.current = e.clientY;
      tracking.current = true;
      claimed.current = false;
      window.addEventListener('mouseup', onMouseUp, true);
    };

    // passive:false is REQUIRED so touchmove can preventDefault() and claim the
    // gesture. touchstart/touchend stay passive:false too for symmetry.
    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: false });
    el.addEventListener('touchcancel', onTouchCancel, { passive: false });
    el.addEventListener('mousedown', onMouseDown);

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchCancel);
      el.removeEventListener('mousedown', onMouseDown);
      // Belt-and-suspenders: drop any in-flight window mouse listener.
      window.removeEventListener('mouseup', onMouseUp, true);
    };
  }, [resolveGesture]);

  const ref = useCallback((el: HTMLElement | null) => {
    // Detach from the previous element first (element swap or unmount).
    if (elRef.current && detachRef.current) {
      detachRef.current();
      detachRef.current = null;
    }
    elRef.current = el;
    if (el) {
      detachRef.current = attach(el);
    }
  }, [attach]);

  return { ref };
}
